import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { ReploError } from "schemas/errors";

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

// Chrome-only experimental API for contextual biasing
// https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition/phrases
interface SpeechRecognitionPhrase {
  phrase: string;
  boost: number;
}

interface SpeechRecognitionPhraseConstructor {
  new (phrase: string, boost?: number): SpeechRecognitionPhrase;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  // Chrome-only: phrases for contextual biasing
  phrases?: SpeechRecognitionPhrase[];
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
    SpeechRecognitionPhrase?: SpeechRecognitionPhraseConstructor;
  }
}

class VoiceInputError extends ReploError {
  constructor(message: string) {
    super({ message });
  }
}

// Replo-specific phrases for contextual biasing in voice recognition
// These help the speech recognition API better understand e-commerce and page-building terminology
// Reference: https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition/phrases
const REPLO_VOICE_PHRASES: Array<{ phrase: string; boost: number }> = [
  // Page Section Types
  { phrase: "hero", boost: 5 },
  { phrase: "hero section", boost: 5 },
  { phrase: "banner", boost: 4 },
  { phrase: "header", boost: 4 },
  { phrase: "footer", boost: 4 },
  { phrase: "navigation", boost: 4 },
  { phrase: "nav bar", boost: 4 },
  { phrase: "above the fold", boost: 4 },
  { phrase: "below the fold", boost: 4 },
  { phrase: "product showcase", boost: 4 },
  { phrase: "testimonials", boost: 4 },
  { phrase: "social proof", boost: 4 },
  { phrase: "FAQ", boost: 5 },
  { phrase: "frequently asked questions", boost: 4 },
  { phrase: "call to action", boost: 4 },
  { phrase: "CTA", boost: 5 },

  // Page Types
  { phrase: "landing page", boost: 5 },
  { phrase: "product page", boost: 5 },
  { phrase: "PDP", boost: 5 },
  { phrase: "product detail page", boost: 4 },
  { phrase: "collection page", boost: 4 },
  { phrase: "homepage", boost: 4 },
  { phrase: "home page", boost: 4 },
  { phrase: "about page", boost: 4 },
  { phrase: "contact page", boost: 4 },
  { phrase: "checkout", boost: 4 },
  { phrase: "cart page", boost: 4 },

  // E-commerce Components
  { phrase: "add to cart", boost: 5 },
  { phrase: "buy box", boost: 5 },
  { phrase: "sticky add to cart", boost: 5 },
  { phrase: "sticky cart", boost: 5 },
  { phrase: "variant selector", boost: 5 },
  { phrase: "variant picker", boost: 5 },
  { phrase: "size selector", boost: 4 },
  { phrase: "color selector", boost: 4 },
  { phrase: "color swatch", boost: 4 },
  { phrase: "swatches", boost: 4 },
  { phrase: "quantity selector", boost: 4 },
  { phrase: "price display", boost: 4 },
  { phrase: "compare at price", boost: 4 },
  { phrase: "sale price", boost: 4 },
  { phrase: "product images", boost: 4 },
  { phrase: "product gallery", boost: 4 },
  { phrase: "image gallery", boost: 4 },
  { phrase: "product description", boost: 4 },
  { phrase: "SKU", boost: 5 },
  { phrase: "inventory", boost: 4 },
  { phrase: "stock", boost: 4 },
  { phrase: "out of stock", boost: 4 },
  { phrase: "in stock", boost: 4 },
  { phrase: "upsell", boost: 4 },
  { phrase: "cross sell", boost: 4 },
  { phrase: "bundle", boost: 4 },
  { phrase: "subscription", boost: 4 },
  { phrase: "subscribe and save", boost: 4 },

  // Product Data
  { phrase: "product", boost: 4 },
  { phrase: "variant", boost: 5 },
  { phrase: "collection", boost: 4 },
  { phrase: "metafield", boost: 5 },
  { phrase: "metafields", boost: 5 },
  { phrase: "dynamic data", boost: 4 },
  { phrase: "Shopify", boost: 5 },
  { phrase: "liquid", boost: 4 },

  // UI Components
  { phrase: "carousel", boost: 5 },
  { phrase: "slider", boost: 5 },
  { phrase: "sliding carousel", boost: 5 },
  { phrase: "image slider", boost: 5 },
  { phrase: "product carousel", boost: 5 },
  { phrase: "accordion", boost: 5 },
  { phrase: "collapsible", boost: 5 },
  { phrase: "tabs", boost: 4 },
  { phrase: "tab component", boost: 4 },
  { phrase: "modal", boost: 4 },
  { phrase: "popup", boost: 4 },
  { phrase: "drawer", boost: 4 },
  { phrase: "slide out", boost: 4 },
  { phrase: "dropdown", boost: 4 },
  { phrase: "tooltip", boost: 4 },
  { phrase: "marquee", boost: 5 },
  { phrase: "ticker", boost: 4 },
  { phrase: "countdown", boost: 4 },
  { phrase: "countdown timer", boost: 4 },
  { phrase: "timer", boost: 4 },
  { phrase: "progress bar", boost: 4 },
  { phrase: "star rating", boost: 4 },
  { phrase: "rating", boost: 4 },
  { phrase: "badge", boost: 4 },
  { phrase: "tag", boost: 4 },
  { phrase: "pill", boost: 4 },
  { phrase: "chip", boost: 4 },

  // Media
  { phrase: "image", boost: 3 },
  { phrase: "video", boost: 3 },
  { phrase: "video player", boost: 4 },
  { phrase: "YouTube", boost: 5 },
  { phrase: "Vimeo", boost: 5 },
  { phrase: "embed", boost: 4 },
  { phrase: "iframe", boost: 4 },
  { phrase: "icon", boost: 3 },
  { phrase: "SVG", boost: 4 },
  { phrase: "GIF", boost: 4 },
  { phrase: "animation", boost: 4 },
  { phrase: "Lottie", boost: 5 },

  // Integrations - Reviews
  { phrase: "Okendo", boost: 5 },
  { phrase: "Junip", boost: 5 },
  { phrase: "Yotpo", boost: 5 },
  { phrase: "Loox", boost: 5 },
  { phrase: "Judge.me", boost: 5 },
  { phrase: "Stamped", boost: 5 },
  { phrase: "reviews", boost: 4 },
  { phrase: "product reviews", boost: 4 },
  { phrase: "star reviews", boost: 4 },

  // Integrations - Marketing
  { phrase: "Klaviyo", boost: 5 },
  { phrase: "Postscript", boost: 5 },
  { phrase: "Attentive", boost: 5 },
  { phrase: "SMS", boost: 4 },
  { phrase: "email signup", boost: 4 },
  { phrase: "newsletter", boost: 4 },
  { phrase: "email capture", boost: 4 },
  { phrase: "form", boost: 3 },
  { phrase: "input", boost: 3 },
  { phrase: "submit", boost: 3 },

  // Integrations - Other
  { phrase: "Rebuy", boost: 5 },
  { phrase: "Recharge", boost: 5 },
  { phrase: "Bold", boost: 4 },
  { phrase: "Skio", boost: 5 },
  { phrase: "Loop", boost: 4 },
  { phrase: "Gorgias", boost: 5 },
  { phrase: "Zendesk", boost: 5 },
  { phrase: "Triple Whale", boost: 5 },
  { phrase: "Elevar", boost: 5 },
  { phrase: "Intelligems", boost: 5 },

  // Industries/Verticals
  { phrase: "beauty", boost: 3 },
  { phrase: "skincare", boost: 3 },
  { phrase: "supplements", boost: 3 },
  { phrase: "nutraceuticals", boost: 4 },
  { phrase: "fashion", boost: 3 },
  { phrase: "apparel", boost: 3 },
  { phrase: "DTC", boost: 5 },
  { phrase: "D2C", boost: 5 },
  { phrase: "direct to consumer", boost: 4 },
  { phrase: "ecommerce", boost: 4 },
  { phrase: "e-commerce", boost: 4 },

  // Layout & Structure
  { phrase: "container", boost: 4 },
  { phrase: "columns", boost: 4 },
  { phrase: "grid", boost: 4 },
  { phrase: "flexbox", boost: 4 },
  { phrase: "flex", boost: 3 },
  { phrase: "row", boost: 3 },
  { phrase: "column", boost: 3 },
  { phrase: "section", boost: 3 },
  { phrase: "wrapper", boost: 3 },
  { phrase: "spacer", boost: 3 },
  { phrase: "divider", boost: 3 },
  { phrase: "padding", boost: 3 },
  { phrase: "margin", boost: 3 },
  { phrase: "gap", boost: 3 },
  { phrase: "responsive", boost: 4 },
  { phrase: "mobile", boost: 4 },
  { phrase: "desktop", boost: 4 },
  { phrase: "tablet", boost: 4 },
  { phrase: "breakpoint", boost: 4 },

  // Actions
  { phrase: "publish", boost: 4 },
  { phrase: "save", boost: 3 },
  { phrase: "duplicate", boost: 4 },
  { phrase: "copy", boost: 3 },
  { phrase: "paste", boost: 3 },
  { phrase: "delete", boost: 3 },
  { phrase: "undo", boost: 3 },
  { phrase: "redo", boost: 3 },
  { phrase: "preview", boost: 4 },
  { phrase: "edit", boost: 3 },
  { phrase: "add", boost: 3 },
  { phrase: "remove", boost: 3 },
  { phrase: "move", boost: 3 },
  { phrase: "resize", boost: 3 },
  { phrase: "drag", boost: 3 },
  { phrase: "drop", boost: 3 },

  // Replo-specific
  { phrase: "Replo", boost: 5 },
  { phrase: "component", boost: 4 },
  { phrase: "template", boost: 4 },
  { phrase: "symbol", boost: 4 },
  { phrase: "state", boost: 3 },
  { phrase: "interaction", boost: 4 },
  { phrase: "hover", boost: 3 },
  { phrase: "click", boost: 3 },
  { phrase: "on click", boost: 4 },
  { phrase: "on hover", boost: 4 },
];

// Web Speech API error codes mapped to user-friendly messages
const SPEECH_ERROR_MESSAGES: Record<string, string | null> = {
  "not-allowed":
    "Microphone access denied. Please allow microphone permissions.",
  "permission-denied":
    "Microphone access denied. Please allow microphone permissions.",
  "no-speech": null, // Not really an error - just no speech detected
  "audio-capture": "No microphone found. Please connect a microphone.",
  network: "Network error occurred. Please check your connection.",
  aborted: null, // User aborted - not an error
};

interface UseVoiceInputOptions {
  lang?: string;
  onTranscriptComplete: (transcript: string) => void;
  onTranscriptUpdate: (transcript: string) => void;
}

interface UseVoiceInputResult {
  isRecording: boolean;
  isSupported: boolean;
  transcript: string;
  interimTranscript: string;
  startRecording: () => void;
  stopRecording: () => void;
  error: string | null;
}

export function useVoiceInput({
  lang = "en-US",
  onTranscriptComplete,
  onTranscriptUpdate,
}: UseVoiceInputOptions): UseVoiceInputResult {
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef("");
  const onTranscriptCompleteRef = useRef(onTranscriptComplete);
  const onTranscriptUpdateRef = useRef(onTranscriptUpdate);

  useEffect(() => {
    onTranscriptCompleteRef.current = onTranscriptComplete;
  }, [onTranscriptComplete]);

  useEffect(() => {
    onTranscriptUpdateRef.current = onTranscriptUpdate;
  }, [onTranscriptUpdate]);

  const isSupported =
    typeof window !== "undefined" &&
    (Boolean(window.SpeechRecognition) ||
      Boolean(window.webkitSpeechRecognition));

  const mutation = useMutation({
    mutationFn: (): Promise<string> => {
      return new Promise((resolve, reject) => {
        if (!isSupported) {
          reject(
            new VoiceInputError("Voice input is not supported in this browser"),
          );
          return;
        }

        setTranscript("");
        setInterimTranscript("");
        finalTranscriptRef.current = "";

        const SpeechRecognitionClass =
          window.SpeechRecognition ?? window.webkitSpeechRecognition;

        if (!SpeechRecognitionClass) {
          reject(
            new VoiceInputError("Voice input is not supported in this browser"),
          );
          return;
        }

        const recognition = new SpeechRecognitionClass();
        recognitionRef.current = recognition;

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = lang;

        // Apply contextual biasing phrases if supported (Chrome-only experimental feature)
        // https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition/phrases
        if ("phrases" in recognition && window.SpeechRecognitionPhrase) {
          try {
            recognition.phrases = REPLO_VOICE_PHRASES.map(
              ({ phrase, boost }) => {
                return new window.SpeechRecognitionPhrase!(phrase, boost);
              },
            );
          } catch {
            // Silently fail if phrases are not supported
            // This is expected on browsers other than Chrome
          }
        }

        recognition.onresult = (event: SpeechRecognitionEvent) => {
          let interim = "";
          let final = "";

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result?.[0]) {
              const transcriptText = result[0].transcript;
              if (result.isFinal) {
                final += transcriptText;
              } else {
                interim += transcriptText;
              }
            }
          }

          if (final) {
            finalTranscriptRef.current += final;
            setTranscript(finalTranscriptRef.current);
          }

          setInterimTranscript(interim);

          const currentFullTranscript = finalTranscriptRef.current + interim;
          if (currentFullTranscript) {
            onTranscriptUpdateRef.current?.(currentFullTranscript);
          }
        };

        recognition.addEventListener("error", (event) => {
          const errorEvent = event as SpeechRecognitionErrorEvent;
          const mappedError = SPEECH_ERROR_MESSAGES[errorEvent.error];
          if (mappedError === null) {
            // Not a real error (e.g., no-speech, aborted)
            return;
          }
          const errorMessage =
            mappedError ?? `Voice input error: ${errorEvent.error}`;
          reject(new VoiceInputError(errorMessage));
        });

        recognition.onend = () => {
          setInterimTranscript("");
          recognitionRef.current = null;

          const completedTranscript = finalTranscriptRef.current.trim();
          if (completedTranscript) {
            onTranscriptCompleteRef.current?.(completedTranscript);
          }
          resolve(completedTranscript);
        };

        try {
          recognition.start();
        } catch {
          reject(
            new VoiceInputError(
              "Failed to start voice input. Please try again.",
            ),
          );
        }
      });
    },
  });

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const startRecording = useCallback(() => {
    mutation.reset();
    mutation.mutate();
  }, [mutation]);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
    };
  }, []);

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return {
    isRecording: mutation.isPending,
    isSupported,
    transcript,
    interimTranscript,
    startRecording,
    stopRecording,
    error: errorMessage,
  };
}
